import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type { NavigationGroupIDProp } from './NavigationGroup';

type NavigationGroupCollapsedProp = boolean;

// Unified navigation context that handles both group-specific and global navigation state
interface NavigationContextData {
  // Group-specific state (for individual NavigationGroup instances)
  id: NavigationGroupIDProp;
  collapsible: boolean;
  collapsed: NavigationGroupCollapsedProp;
  setCollapsed: (value: NavigationGroupCollapsedProp) => void;

  // Global state (shared across all groups, for minimized mode)
  openGroupId: string | null;
  setOpenGroupId: (id: string | null) => void;
  focusedGroupId: string | null;
  setFocusedGroupId: (id: string | null) => void;
}

// Root-level context that only contains global state
interface NavigationGlobalContextData {
  openGroupId: string | null;
  setOpenGroupId: (id: string | null) => void;
  focusedGroupId: string | null;
  setFocusedGroupId: (id: string | null) => void;
}

const NavigationGlobalContext = createContext<NavigationGlobalContextData>({
  openGroupId: null,
  setOpenGroupId: () => {},
  focusedGroupId: null,
  setFocusedGroupId: () => {},
});

const NavigationGroupContext = createContext<NavigationContextData>({
  id: '',
  collapsible: false,
  collapsed: false,
  setCollapsed: () => {},
  openGroupId: null,
  setOpenGroupId: () => {},
  focusedGroupId: null,
  setFocusedGroupId: () => {},
});

// Root provider that manages global navigation state
export function NavigationProvider({ children }: { children: ReactNode }) {
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const [focusedGroupId, setFocusedGroupId] = useState<string | null>(null);

  const value = useMemo(
    () => ({
      openGroupId,
      setOpenGroupId,
      focusedGroupId,
      setFocusedGroupId,
    }),
    [openGroupId, focusedGroupId],
  );

  return (
    <NavigationGlobalContext.Provider value={value}>{children}</NavigationGlobalContext.Provider>
  );
}

// Group-specific provider that combines global state with group-specific state
export function NavigationGroupsProvider({
  children,
  collapsible,
  id,
  collapsedState,
  setCollapsedState,
}: {
  collapsible?: boolean;
  children?: ReactNode;
  id?: NavigationGroupIDProp;
  collapsedState?: boolean;
  setCollapsedState?: React.Dispatch<boolean>;
}) {
  const [innerCollapsed, setInnerCollapsed] = useState(false);
  const globalContext = useContext(NavigationGlobalContext);

  const value = useMemo(
    () => ({
      collapsible: collapsible || false,
      id: id || '',
      collapsed: collapsedState ?? innerCollapsed,
      setCollapsed: setCollapsedState ?? setInnerCollapsed,
      // Include global state in the group context
      openGroupId: globalContext.openGroupId,
      setOpenGroupId: globalContext.setOpenGroupId,
      focusedGroupId: globalContext.focusedGroupId,
      setFocusedGroupId: globalContext.setFocusedGroupId,
    }),
    [
      collapsible,
      collapsedState,
      id,
      setCollapsedState,
      globalContext,
      innerCollapsed,
      setInnerCollapsed,
    ],
  );

  return (
    <NavigationGroupContext.Provider value={value}>{children}</NavigationGroupContext.Provider>
  );
}

export function useNavigationGroupsContext() {
  const groupContext = useContext(NavigationGroupContext);
  const globalContext = useContext(NavigationGlobalContext);

  // If we're not in a NavigationGroupsProvider (group context has empty id),
  // merge the global context state with the group context defaults
  if (!groupContext.id) {
    return {
      ...groupContext,
      openGroupId: globalContext.openGroupId,
      setOpenGroupId: globalContext.setOpenGroupId,
      focusedGroupId: globalContext.focusedGroupId,
      setFocusedGroupId: globalContext.setFocusedGroupId,
    };
  }

  return groupContext;
}
